import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import api from "../../api/api";
import { useAuth } from "../../hooks/useAuth";
import { Alert, PageHeader, Panel, Pill, apiError, useOrgStyles, ORG_TYPE_LABEL } from "../../components/org/orgUi";
//old import { ORG_PAGES, orgPagePath } from "./orgPortalContext";
import { ORG_PAGES, groupedOrgPages, orgPagePath } from "./orgPortalContext";

/**
 * Shell for the organization portal (org admins, licensed org coaches, and
 * Pono Pilot admins arriving from Admin -> Organizations with ?orgId=). Loads the
 * org once and renders the current page (/org, /org/programs, ...) via <Outlet>.
 */
export default function OrgLayout() {
    const s = useOrgStyles();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [context, setContext] = useState(null);
    const [org, setOrg] = useState(null);
    const [viewer, setViewer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // with no org to show, what to say depends on the context (a suspended org's admin is told so)
    const [contextDone, setContextDone] = useState(false);

    const requestedOrgId = Number(searchParams.get("orgId")) || null;
    const accessibleOrgs = useMemo(
        //old () => (context?.organizations || []).filter((o) => o.IsOrgAdmin || o.HasCoachLicense),
        // (the list also has the suspended or archived orgs this person is an org admin of, Status 'suspended':
        // they can't be opened)
        () => (context?.organizations || []).filter((o) => o.Status !== "suspended" && (o.IsOrgAdmin || o.HasCoachLicense)),
        [context]
    );
    const orgId = requestedOrgId || accessibleOrgs[0]?.OrgID || null;

    useEffect(() => {
        api.get("/org/context")
            .then((res) => setContext(res.data))
            .catch((err) => setError(apiError(err, "Couldn't load your organizations.")))
            //old .finally(() => { if (!orgId) setLoading(false); });
            .finally(() => { setContextDone(true); if (!orgId) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrg = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get(`/org/${orgId}`);
            setOrg(res.data.organization);
            setViewer(res.data.viewer);
            setError("");
        } catch (err) {
            setOrg(null);
            setError(apiError(err, "Couldn't load this organization."));
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { setLoading(true); loadOrg(); }, [loadOrg]);

    // Old links used /org?tab=<page>; send them to the page's own route.
    const legacyTab = searchParams.get("tab");
    if (legacyTab) {
        const { tab, orgId: legacyOrgId, ...rest } = Object.fromEntries(searchParams);
        return <Navigate to={orgPagePath(tab, { orgId: legacyOrgId, ...rest })} replace />;
    }

    //old if (loading && !org) {
    if ((loading || !contextDone) && !org) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Loader2 className={`animate-spin ${s.muted}`} size={28} />
            </div>
        );
    }

    if (!orgId || !org) {
        // An org admin whose organization was suspended or archived is told so, not that they aren't part of
        // one: the org asked for (an emailed link), or, with no org to open, the one they're an admin of
        const orgs = context?.organizations || [];
        const suspended = requestedOrgId
            ? orgs.find((o) => o.OrgID === requestedOrgId && o.Status === "suspended")
            : !orgs.some((o) => o.Status !== "suspended") && orgs.find((o) => o.Status === "suspended");
        return (
            <div className="mx-auto max-w-3xl px-4 py-10">
                <PageHeader title="Organization" />
                {/* old <Alert>{error}</Alert> */}
                {/* (it can't be opened, so its "not found" isn't shown) */}
                <Alert>{suspended ? "" : error}</Alert>
                <Panel>
                    <div className="py-6 text-center">
                        <Building2 className={`mx-auto mb-3 ${s.muted}`} size={32} />
                        <p className={s.body}>
                            {context?.isPlatformAdmin
                                ? <>Open an organization from <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                                : suspended
                                    ? <>Your organization is suspended. Contact <a className={s.btnLink} href="mailto:hello@ponopilot.com">hello@ponopilot.com</a>.</>
                                : (context?.organizations || []).length
                                    ? "Your organization hasn't given you a coach license for this month yet. Ask your organization admin."
                                    : "You aren't part of an organization yet."}
                        </p>
                    </div>
                </Panel>
            </div>
        );
    }

    const isOrgAdmin = !!viewer?.isOrgAdmin;
    const isPlatformAdmin = !!viewer?.isPlatformAdmin;
    const pages = ORG_PAGES.filter((p) => isOrgAdmin || !p.adminOnly);
    const segment = location.pathname.replace(/^\/org\/?/, "").split("/")[0];
    const current = ORG_PAGES.find((p) => p.path === segment);
    if (!current) return <Navigate to={orgPagePath("overview", { orgId: requestedOrgId })} replace />;
    if (current.adminOnly && !isOrgAdmin) return <Navigate to={orgPagePath("overview", { orgId: requestedOrgId })} replace />;

    const goTo = (key, extra = {}) => navigate(orgPagePath(key, { orgId: requestedOrgId, ...extra }));
    const switchOrg = (id) => navigate(orgPagePath(current.key, { orgId: id }));
    // Org-admin-only logins reach each page from the sidebar. Coaches and
    // Pono Pilot admins don't have those sidebar links, so they get page links here.
    const showPageLinks = !(user?.isOrgAdmin && !user?.isCoach && !user?.isAdmin);

    return (
        <div className="mx-auto max-w-6xl px-0 md:px-4 py-2 md:py-6">
            <PageHeader
                title={current.key === "overview" ? org.OrgName : current.label}
                subtitle={
                    <span className="inline-flex flex-wrap items-center gap-2">
                        {current.key !== "overview" && <span className={s.body}>{org.OrgName}</span>}
                        <Pill tone="primary">{ORG_TYPE_LABEL[org.OrgType] || org.OrgType}</Pill>
                        {org.Status !== "active" && <Pill tone="warning">{org.Status}</Pill>}
                        {isPlatformAdmin && <Pill tone="neutral">Viewing as Pono Pilot admin</Pill>}
                        {!isOrgAdmin && <Pill tone="neutral">Coach view</Pill>}
                    </span>
                }
                actions={accessibleOrgs.length > 1 && (
                    <select
                        aria-label="Switch organization"
                        value={orgId}
                        onChange={(e) => switchOrg(e.target.value)}
                        //old code
                        //className={`${s.input} w-auto`}
                        // s.input's w-full beats w-auto, so on a phone it stretched across the header
                        className={`${s.input} max-w-[14rem]`}
                    >
                        {accessibleOrgs.map((o) => <option key={o.OrgID} value={o.OrgID}>{o.OrgName}</option>)}
                    </select>
                )}
            />
            {showPageLinks && (
                //old <nav aria-label="Organization pages" className="mb-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
                    //old {pages.map((p) => (
                        //old <NavLink
                            //old key={p.key}
                            //old to={orgPagePath(p.key, { orgId: requestedOrgId })}
                            //old end={p.key === "overview"}
                            //old className={({ isActive }) => isActive ? (s.dark ? "text-white" : "text-primary-700") : s.muted}
                        //old >
                            //old {p.label}
                        //old </NavLink>
                    //old ))}
                //old </nav>
                // Oct 16 (Evan): grouped like the org admin's sidebar, from the same groupedOrgPages
                <nav aria-label="Organization pages" className="mb-6 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold">
                    {groupedOrgPages(pages).map((g) => (
                        <div key={g.key} role="group" aria-labelledby={`org-pages-${g.key}`} className="flex flex-col gap-1">
                            {/* Overview has no group name; an invisible one keeps its link on the same line as the others */}
                            <span id={`org-pages-${g.key}`} className={`text-[11px] font-bold uppercase tracking-wide ${s.muted} ${g.label ? "" : "invisible"}`}>
                                {g.label || g.pages[0].label}
                            </span>
                            <div className="flex flex-wrap gap-x-5 gap-y-1">
                                {g.pages.map((p) => (
                                    <NavLink
                                        key={p.key}
                                        to={orgPagePath(p.key, { orgId: requestedOrgId })}
                                        end={p.key === "overview"}
                                        className={({ isActive }) => isActive ? (s.dark ? "text-white" : "text-primary-700") : s.muted}
                                    >
                                        {p.label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
            )}
            <Alert onClose={() => setError("")}>{error}</Alert>
            {/* old <Outlet context={{ orgId, org, viewer, isOrgAdmin, isPlatformAdmin, reloadOrg: loadOrg, goTo }} /> */}
            {/* A fresh page for each loaded org: switching orgs doesn't carry one org's form or report into
                another (and saves go to the org on screen) */}
            <Outlet key={org?.OrgID} context={{ orgId: org?.OrgID ?? orgId, org, viewer, isOrgAdmin, isPlatformAdmin, reloadOrg: loadOrg, goTo }} />
        </div>
    );
}
